/* Zoekformulier — JD-textarea, filters, seniority-pills, bron-selector,
 * doelpool-keuze en de zoek-CTA + resultaat-infobalk. */
import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Search as SearchIcon, Sparkles, Loader2, Filter, Wand2, UserSearch } from "lucide-react";
import { SourceSelector } from "@/components/recruit/SourceSelector";
import { COUNTRIES, SIZES, sourceLabelOf } from "./constants";

export default function SearchForm({
    jd, setJd, filters, setFilters,
    searchSources, setSearchSources,
    targetPool, setTargetPool, pools,
    searching, lastSearchInfo, onRun, onOpenLookalike,
}) {
    const { t } = useTranslation();
    return (
        <div className="rounded-xl border border-[var(--jr-amber)]/30 bg-gradient-to-br from-[var(--jr-amber)]/10 to-transparent p-5 mb-4">
            <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1">
                <Sparkles size={11} className="text-[var(--jr-amber)]"/>{t("search.jd_label")}
            </label>
            <textarea value={jd} onChange={(e) => setJd(e.target.value)} rows={3} data-testid="talent-jd"
                placeholder={t("search.jd_placeholder")}
                className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-[var(--jr-amber)] outline-none"/>

            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Input label={t("search.skill_label")} v={filters.skill} onChange={(v) => setFilters({...filters, skill: v})} testid="filter-skill" placeholder="bv. Python"/>
                <Select label={t("search.country_label")} v={filters.country} onChange={(v) => setFilters({...filters, country: v})} testid="filter-country" options={COUNTRIES} />
                <Select label={t("search.size_label")} v={filters.size} onChange={(v) => setFilters({...filters, size: v})} testid="filter-size" options={SIZES} />
                <Input label={t("search.sector_label")} v={filters.sector} onChange={(v) => setFilters({...filters, sector: v})} testid="filter-sector" placeholder="bv. SaaS, Fintech"/>
            </div>

            {/* Seniority pills */}
            <div className="mt-3">
                <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-2">
                    {t("search.seniority_label")}
                    {filters.seniority.length > 0 && (
                        <>
                            <span className="text-[10px] font-mono normal-case tracking-normal px-1.5 py-0.5 rounded bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]"
                                data-testid="seniority-count">{filters.seniority.length} gekozen</span>
                            <button onClick={() => setFilters({...filters, seniority: []})}
                                data-testid="seniority-clear"
                                className="text-[10px] normal-case tracking-normal text-[var(--jr-text-3)] hover:text-white underline">
                                Reset
                            </button>
                        </>
                    )}
                </label>
                <div className="mt-2 flex flex-wrap gap-1" data-testid="filter-seniority">
                    {[
                        ["junior", "Junior"],
                        ["medior", "Medior"],
                        ["senior", "Senior"],
                        ["lead", "Lead"],
                        ["executive", "Executive"],
                    ].map(([v, l]) => {
                        const active = filters.seniority.includes(v);
                        return (
                            <button key={v} type="button" onClick={() => {
                                const next = active
                                    ? filters.seniority.filter((x) => x !== v)
                                    : [...filters.seniority, v];
                                setFilters({...filters, seniority: next});
                            }} data-testid={`seniority-${v}`}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active
                                    ? "bg-[var(--jr-amber)] text-black border-[var(--jr-amber)]"
                                    : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white"}`}>
                                {active && <span className="mr-1">✓</span>}{l}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Bron-selector + doelpool + CTA */}
            <SourceSelector selected={searchSources}
                onToggle={(id) => setSearchSources((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
                onInit={(ids) => setSearchSources(ids)} />
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                {/* Doelpool — nieuwe kandidaten belanden hier */}
                <label className="flex items-center gap-2" data-testid="target-pool-picker">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("search.save_to_pool")}</span>
                    <select value={targetPool} onChange={(e) => setTargetPool(e.target.value)} data-testid="target-pool-select"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none focus:border-[var(--jr-amber)]">
                        {(pools.length > 0 ? pools : [{ key: "external", name: i18n.t("search.pool_external") }]).map((p) => (
                            <option key={p.key} value={p.key}>{p.name}</option>
                        ))}
                    </select>
                </label>
                <div className="flex items-center gap-2">
                    <button onClick={onOpenLookalike}
                        data-testid="lookalike-open"
                        title={i18n.t("lookalike.subtitle_url")}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#a78bfa]/50 text-[#a78bfa] text-sm font-medium hover:bg-[#a78bfa]/10">
                        <UserSearch size={14} /> {i18n.t("lookalike.title")}
                    </button>
                    <button onClick={onRun} disabled={searching || jd.trim().length < 3 || searchSources.length === 0}
                        data-testid="talent-search-run"
                        title={searchSources.length === 0 ? i18n.t("search.pick_sources") : ""}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--jr-amber)] text-black text-sm font-semibold hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                        {searching ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
                        {searching ? t("search.searching_btn") : t("search.search_btn")}
                    </button>
                </div>
            </div>

            {lastSearchInfo && !searching && (
                <div className="mt-3 rounded-md border border-[var(--jr-success)]/30 bg-[var(--jr-success)]/[0.06] px-3 py-2 text-xs flex items-center gap-2 flex-wrap"
                    data-testid="talent-search-info">
                    <SearchIcon size={12} className="text-[var(--jr-success)]" />
                    <span className="text-[var(--jr-success)] font-medium">{lastSearchInfo.new} nieuwe · {lastSearchInfo.reused} reeds bekend</span>
                    {/* Per-bron resultaat-chips */}
                    {(lastSearchInfo.sources_used || []).map((s) => (
                        <span key={s.id} data-testid={`src-result-${s.id}`}
                            title={s.error || `${sourceLabelOf(s.id)}: ${s.found} ${i18n.t("search.src_results")}`}
                            className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${s.error
                                ? "border-[var(--jr-error)]/40 text-[var(--jr-error)] bg-[var(--jr-error)]/10"
                                : "border-white/10 text-[var(--jr-text-2)]"}`}>
                            {sourceLabelOf(s.id)} · {s.error ? "×" : s.found}
                        </span>
                    ))}
                    {lastSearchInfo.titles.length > 0 && (
                        <span className="text-[var(--jr-text-3)] font-mono text-[10px]">titles: {lastSearchInfo.titles.slice(0, 3).join(", ")}</span>
                    )}
                    <button onClick={() => setFilters({...filters, onlyNew: !filters.onlyNew})}
                        data-testid="filter-only-new"
                        className={`ml-auto text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${filters.onlyNew ? "bg-[var(--jr-amber)] text-black border-[var(--jr-amber)]" : "border-white/15 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                        <Filter size={9} className="inline mr-1" /> {filters.onlyNew ? i18n.t("search.show_all") : i18n.t("search.only_new")}
                    </button>
                    {(lastSearchInfo.sources_used || []).filter((s) => s.error).map((s) => (
                        <div key={`err-${s.id}`} className="basis-full text-[10px] text-[var(--jr-amber)] font-mono pt-1 border-t border-white/5">
                            ⚠ {sourceLabelOf(s.id)}: {s.error}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Input({ label, v, onChange, testid, placeholder }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <input value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
                className="mt-1.5 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none"/>
        </div>
    );
}

function Select({ label, v, onChange, testid, options }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <select value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="mt-1.5 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </div>
    );
}
